import React, { useEffect, useState } from 'react';
import { BedDouble } from 'lucide-react';
import { api, type LodgingQuote, type LodgingStay, type MarkLodging, type QidUser } from '../../api';
import { RADIUS, useTheme } from '../../theme';
import { Badge, Button, Icon } from '../../components/ui';
import { LodgingStayPicker } from './LodgingStayPicker';
import { LodgingImages, LodgingInstructions } from './LodgingInfo';
import { DEFAULT_CANCELLATION, capacityText, emptyStay, extraText, lodgingBlockedText, lodgingPriceText } from './lodging';

/**
 * Boende på marken (L1) på jaktsidan. Utan annons kan boendet bokas fristående och dras direkt.
 * På en annons med boende visas bara det boendet; det köps i bokningsrutan tillsammans med jakten.
 */
export function LodgingSection({ markId, lodgings: given, user, onLogin, inAd }: {
  markId: string; lodgings?: MarkLodging[]; user: QidUser | null | undefined; onLogin?: () => void;
  /** Annonsens boende: visa uppgifterna, köpet sker i bokningsrutan. */
  inAd?: boolean;
}) {
  const [list, setList] = useState<MarkLodging[]>(given ?? []);
  useEffect(() => {
    if (given) { setList(given); return; }
    let off = false;
    api.markLodgings(markId).then(l => { if (!off) setList(l); }).catch(() => {});
    return () => { off = true; };
  }, [markId, given]);
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="lodging-section">
      {list.map(l => <LodgingCard key={l.id} lodging={l} user={user} onLogin={onLogin} inAd={inAd} />)}
    </div>
  );
}

function LodgingCard({ lodging: l, user, onLogin, inAd }: { lodging: MarkLodging; user: QidUser | null | undefined; onLogin?: () => void; inAd?: boolean }) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const [stay, setStay] = useState<LodgingStay>(() => emptyStay(l));
  const [quote, setQuote] = useState<LodgingQuote | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const linens = extraText(l.linensIncluded, l.linensPrice, l.linensUnit);
  const cleaning = extraText(l.cleaningIncluded, l.cleaningPrice, l.cleaningUnit);
  const facts = [
    ['Pris', lodgingPriceText(l)],
    ['Plats', capacityText(l)],
    ['Lakan och handdukar', linens ?? 'Ingår inte'],
    ['Städning', cleaning ?? 'Ingår inte'],
    (l.checkInTime || l.checkOutTime) ? ['In- och utcheckning', [l.checkInTime && `in från ${l.checkInTime}`, l.checkOutTime && `ut senast ${l.checkOutTime}`].filter(Boolean).join(', ')] : null,
  ].filter((x): x is string[] => !!x);

  const pay = async () => {
    setErr('');
    if (!user) { onLogin?.(); return; }
    setBusy(true);
    try {
      const { sessionUrl } = await api.startLodgingCheckout(stay, window.location.pathname);
      window.location.href = sessionUrl;
    } catch (e: any) {
      if (e?.code === 'login') onLogin?.();
      else setErr(e?.message || 'Kunde inte starta bokningen');
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 18, borderRadius: RADIUS.lg, background: T.surface, border: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="lodging-card">
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span aria-hidden style={{ width: 40, height: 40, borderRadius: 10, background: T.accentSoft, color: T.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon as={BedDouble} size={20} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{l.name}</div>
          {l.description && <div style={{ fontSize: 14, color: T.textSub, marginTop: 4, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{l.description}</div>}
        </div>
        {inAd && <Badge tone="accent">Kan läggas till</Badge>}
      </div>
      <LodgingImages images={l.images ?? []} name={l.name} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '4px 24px' }}>
        {facts.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: `1px solid ${T.border}`, fontSize: 14 }}>
            <span style={{ color: T.textSub }}>{k}</span><b style={{ color: T.text, textAlign: 'right' }}>{v}</b>
          </div>
        ))}
      </div>
      <LodgingInstructions info={l} />
      <div style={{ fontSize: 13, color: T.textSub }}><b style={{ color: T.text }}>Avbokning: </b>{l.cancellationPolicy || DEFAULT_CANCELLATION}</div>

      {inAd ? (
        <div style={{ fontSize: 13, color: T.textSub }}>Lägg till boendet när du köper jakten – det reserveras och dras tillsammans med jakten.</div>
      ) : !l.bookable ? (
        <div style={{ fontSize: 13, color: T.textSub }}>{lodgingBlockedText(l.bookBlockedReason)}</div>
      ) : !open ? (
        <Button variant="secondary" onClick={() => setOpen(true)} data-testid="lodging-open">Välj datum och boka</Button>
      ) : (
        <>
          <LodgingStayPicker lodging={l} value={stay} onChange={setStay} onQuote={setQuote} />
          <Button block size="lg" loading={busy} disabled={!quote?.available} onClick={pay} data-testid="lodging-pay">
            {user ? (quote ? `Boka och betala ${quote.amount.toLocaleString('sv-SE')} kr` : 'Boka och betala') : 'Logga in för att boka'}
          </Button>
          <div style={{ fontSize: 12, color: T.textSub }}>Beloppet dras direkt med kort via Stripe och går till markägaren.</div>
        </>
      )}
      {err && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: T.danger }}>{err}</div>}
    </div>
  );
}
